import styled, { css, keyframes } from 'styled-components';

import {
    floorGameDuration as duration,
    floorGameEasing as easing,
    styledInteraction,
    styledReset,
} from '@pkg/primitives';

import { tokens } from './SimpleBlobBg.tokens';

const layerBase = css`
    position: absolute;
    aspect-ratio: 1;
    border-radius: 40%;
    animation-duration: ${tokens.duration.layer}ms;
    animation-timing-function: ease-in-out;
    animation-iteration-count: infinite;
    animation-fill-mode: both;
`;

const motionBg = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const FixedBackground = styled.div`
    ${styledReset.borderBox};
    ${styledInteraction.interactionDisable};
    z-index: -1;
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    place-content: center;
    opacity: 0;
    background-color: ${tokens.color.bg};
    animation: ${motionBg} ${duration.crawl}ms ${easing.cubic} both;
`;

export const SvgNoiseWrapper = styled.div`
    position: absolute;
    inset: 0;
    opacity: 0.2;
    mix-blend-mode: overlay;

    svg {
        all: unset;
        width: 100%;
        height: 100%;
    }
`;

///
/// Interior

interface InteriorProps {
    $noMotion?: boolean;
}

const motionInterior = keyframes`
  from {
    rotate: 0deg;
  }
  to {
    rotate: 360deg;
  }
`;

export const Interior = styled.div<InteriorProps>`
    width: max(100vw, 100vh);
    aspect-ratio: 1;
    filter: blur(100px);
    animation: ${motionInterior} ${tokens.duration.interior}ms ease-in-out infinite both;

    // Dim on mobile for better legibility.
    @media screen and (max-width: 767px) {
        opacity: 0.6;
    }

    ${({ $noMotion }) =>
        $noMotion &&
        css`
            width: 100vw;
            height: 100vh;
            aspect-ratio: unset;
            animation: unset;

            div {
                animation: unset;
                opacity: 0.2;
            }
        `};
`;

///
/// Layer 1

const motionLayer1 = keyframes`
  0%,
  100% {
    opacity: 0.2;
    scale: 1.4 1.2;
    translate: 0vw 0vh;
  }
  20% {
    opacity: 0.4;
    scale: 1 1;
    translate: 60vw 40vh;
  }
  60% {
    opacity: 0.3;
    scale: 1.4 1.2;
    translate: 20vw 80vh;
  }
  80% {
    opacity: 0.5;
    scale: 1.2 1.4;
    translate: -20vw 40vh;
  }
`;

export const Layer1 = styled.div`
    ${layerBase};
    top: 0;
    left: 0;
    width: max(20vw, 400px);
    background-color: ${tokens.color.layer1};
    animation-name: ${motionLayer1};
`;

///
/// Layer 2

const motionLayer2 = keyframes`
  0%,
  100% {
    opacity: 0.4;
    scale: 1 1;
    translate: 0vw 0vh;
  }
  25% {
    opacity: 0.2;
    scale: 1.4 1.2;
    translate: -60vw 40vh;
  }
  50% {
    opacity: 0.6;
    scale: 1.2 1.4;
    translate: -20vw 80vh;
  }
  75% {
    opacity: 0.2;
    scale: 1.3 1.3;
    translate: 20vw 40vh;
  }
`;

export const Layer2 = styled.div`
    ${layerBase};
    top: 0;
    right: 0;
    width: max(20vw, 320px);
    background-color: ${tokens.color.layer2};
    animation-name: ${motionLayer2};
`;

///
/// Layer 3

const motionLayer3 = keyframes`
  0%,
  100% {
    opacity: 0.6;
    scale: 1.2 1.2;
    translate: 0vw 0vh;
  }
  40% {
    opacity: 0.3;
    scale: 1.6 1.8;
    translate: -60vw -40vh;
  }
  60% {
    opacity: 0.4;
    scale: 1.4 1.4;
    translate: -20vw -80vh;
  }
  80% {
    opacity: 0.2;
    scale: 1.8 1.6;
    translate: 0vw -40vh;
  }
`;

export const Layer3 = styled.div`
    ${layerBase};
    bottom: 0;
    right: 0;
    width: max(20vw, 240px);
    background-color: ${tokens.color.layer3};
    animation-name: ${motionLayer3};
`;

///
/// Layer 4

const motionLayer4 = keyframes`
  0%,
  100% {
    opacity: 0.4;
    scale: 1 1;
    translate: 0vw 0vh;
  }
  20% {
    opacity: 0.2;
    scale: 1.4 1.2;
    translate: 60vw -40vh;
  }
  40% {
    opacity: 0.3;
    scale: 1.2 1.4;
    translate: 20vw -80vh;
  }
  60% {
    opacity: 0.2;
    scale: 1.3 1.3;
    translate: 0vw -40vh;
  }
`;

export const Layer4 = styled.div`
    ${layerBase};
    bottom: 0;
    left: 0;
    width: max(20vw, 360px);
    background-color: ${tokens.color.layer4};
    animation-name: ${motionLayer4};
`;

///
/// Layer 5

const motionLayer5 = keyframes`
  0%,
  100% {
    opacity: 0.3;
    scale: 1 1;
    translate: 0vw 0vh;
  }
  25% {
    opacity: 0.2;
    scale: 1.4 1.2;
    translate: -20vw 40vh;
  }
  50% {
    opacity: 0.4;
    scale: 1.2 1.4;
    translate: 40vw 80vh;
  }
  75% {
    opacity: 0.6;
    scale: 1.3 1.3;
    translate: 20vw 40vh;
  }
`;

export const Layer5 = styled.div`
    ${layerBase};
    top: 25%;
    left: 25%;
    width: max(20vw, 400px);
    background-color: ${tokens.color.layer5};
    animation-name: ${motionLayer5};
`;

///
/// Layer 6

const motionLayer6 = keyframes`
  0%,
  100% {
    opacity: 0.2;
    scale: 1 1;
    translate: 0vw 0vh;
  }
  20% {
    opacity: 0.4;
    scale: 1.4 1.2;
    translate: 20vw -40vh;
  }
  50% {
    opacity: 0.2;
    scale: 1.2 1.4;
    translate: -20vw 0vh;
  }
  80% {
    opacity: 0.3;
    scale: 1.3 1.3;
    translate: 20vw 40vh;
  }
`;

export const Layer6 = styled.div`
    ${layerBase};
    top: 75%;
    left: 75%;
    width: max(20vw, 500px);
    background-color: ${tokens.color.layer6};
    animation-name: ${motionLayer6};
`;

///
/// Layer 7

const motionLayer7 = keyframes`
  0%,
  100% {
    opacity: 0.4;
    scale: 1 1;
    translate: 0vw 0vh;
  }
  25% {
    opacity: 0.2;
    scale: 1.6 2;
    translate: -60vw -40vh;
  }
  50% {
    opacity: 0.3;
    scale: 1.4 1.2;
    translate: 40vw 60vh;
  }
  75% {
    opacity: 0.2;
    scale: 1.8 2;
    translate: -40vw -20vh;
  }
`;

export const Layer7 = styled.div`
    ${layerBase};
    top: 50%;
    left: 50%;
    width: max(20vw, 600px);
    background: linear-gradient(
        180deg,
        ${tokens.color.layer7.stop1} 0%,
        ${tokens.color.layer7.stop2} 20%,
        ${tokens.color.layer7.stop3} 40%,
        ${tokens.color.layer7.stop4} 80%
    );
    animation-name: ${motionLayer7};
`;
